import type { CSSProperties } from 'react'

/**
 * The self view is shown the way a mirror shows it (user decision, 2026-10-24): moving to your left moves
 * your picture to the left, as people expect from a selfie camera. It reverses part of SC-158 (d9e3a0da),
 * which had removed the pre-join mirror because the in-game tile did not mirror either; both surfaces
 * mirror now, so the person looks the same either side of the join.
 *
 * Display only. The published track and the background processor are never flipped, so everyone else
 * sees the real, unreversed picture. The known cost is that a branded background's wordmark reads
 * reversed in your own view only.
 *
 * Only ever applied to the `<video>` element itself, never to a tile wrapper: names, icons, covers and
 * placeholders must read normally.
 *
 * Not applied to remote cameras, screen shares (our own included), or Safari's native single-video
 * picture-in-picture, whose window is the browser's own surface and cannot be styled with CSS; there the
 * own camera stays unmirrored.
 */
export const SELF_VIEW_MIRROR_TRANSFORM = 'scaleX(-1)'

export const SELF_VIEW_MIRROR_STYLE: CSSProperties = { transform: SELF_VIEW_MIRROR_TRANSFORM }

/**
 * The style for a camera `<video>`: `base` as is for someone else's camera, with the mirror added for our
 * own. `isLocalCamera` is true only for the local participant's camera, so a call site that also shows
 * a screen share must not pass its screen-share video through here.
 */
export function selfViewVideoStyle(
  isLocalCamera: boolean | undefined,
  base?: CSSProperties
): CSSProperties | undefined {
  if (!isLocalCamera) return base
  return { ...base, ...SELF_VIEW_MIRROR_STYLE }
}
